import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { useRestoreQueryOnPop, useSyncQueryToUrl } from '../../utils/urlState';
import {
    deleteSkuGlobal,
    listMaterials,
    listSkus,
    SKU_KIND_BUNDLE,
    SKU_KIND_SINGLE,
    SkuKind,
    SkuRow,
} from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { CapBadgesCell } from '../../components/CapTypeBadges';
import DeleteCascadeModal from '../../components/DeleteCascadeModal';
import Pagination from '../../components/Pagination';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { i18nText } from '../../utils/i18n';
import { toast } from '../../utils/toast';
import SkuExportModal from './SkuExportModal';
import SkuCreateModal from './SkuCreateModal';
import SkuEditModal from './SkuEditModal';
import SkuImportModal from './SkuImportModal';
import SkuQrModal, { SkuQrTarget } from './SkuQrModal';
import SkuBundleTable from './SkuBundleTable';
import SkuBundleModal from './SkuBundleModal';
import { useCan } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { SkuPropFilter, SkuPropTag } from '../../components/SkuProp';
import { SkuSpecText } from '../../components/SkuSpec';
import { parseSpecParams, specFieldsOf, specValueText, useSkuSchemas } from '../../utils/skuSpec';
import { SKU_HAS_MATERIAL_OPTS, SKU_STATUS_OPTS } from './selectOptions';

const PAGE_SIZE = 20;

// 适配工艺 / 绑定材料 两列按绑定材料逐行对齐 (2026-09-08): 每个材料在两列里各占一行, 行高统一;
// 工艺徽标单行不换行、最多 2 个 (其余收进 +N 悬停展开; 3 个会把表撑出 1280 屏宽), 保证一个材料永远只有一行, 两列不会错位.
const SKU_BIND_ROW_CLS = 'flex h-[26px] items-center';
const SKU_CAPS_LIMIT = 2;

// SKU 库: 以 SKU 为主键 (一物一码) 的平铺检索视图, 与材料库共用同一张主数据.
// 支持 ?xcode= 预填 (材料详情"在 SKU 库中查看 →" / 材料库 SKU 数入口); ?specGroup= 预选规格类型 (SKU 规格模板页「在用 SKU」入口).
// 两个 tab (2026-10-19): 单品 SKU / SKU 集合 (?kind=bundle), 集合的新建/编辑走 SkuBundleModal.
const SKU_TABS: { key: SkuKind; label: string }[] = [
    { key: SKU_KIND_SINGLE, label: '单品 SKU' },
    { key: SKU_KIND_BUNDLE, label: 'SKU 集合' },
];

function SkuList() {
    const canCreate = useCan('material', 'create');
    const canUpdate = useCan('material', 'update');
    const canDelete = useCan('material', 'delete');
    const canManage = canUpdate || canDelete;
    const location = useLocation();
    const history = useHistory();
    // 筛选/页码同步进 URL query, 进材料详情后浏览器后退可还原 (2026-07-30)
    const initQ = new URLSearchParams(location.search);
    const [kind, setKind] = useState<SkuKind>(
        initQ.get('kind') === SKU_KIND_BUNDLE ? SKU_KIND_BUNDLE : SKU_KIND_SINGLE,
    );
    const isBundle = kind === SKU_KIND_BUNDLE;

    const [input, setInput] = useState(initQ.get('keyword') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [xcode, setXcode] = useState(initQ.get('xcode') || '');
    // 表头筛选: 属性 / 状态 / 绑定材料 (与后端 /v1/skus 同语义, 变更即时生效).
    // 第三个筛选原是 CMS 映射, 2026-09-03 改为绑定材料 (与"绑定材料"列的 未绑定 标记对应);
    // CMS 映射维度在 治理 / ID 映射页 仍可筛.
    const [prop, setProp] = useState(initQ.get('prop') || '');
    const [status, setStatus] = useState(initQ.get('status') || '');
    const [hasMaterial, setHasMaterial] = useState(
        initQ.get('hasMaterial') || '',
    );
    // 规格类型 (2026-10-14): groupKey / none = 未选. 选定某类型时该类型字段展开成独立列 (表头取模板 label)
    const [specGroup, setSpecGroup] = useState(initQ.get('specGroup') || '');
    const { list: specSchemas, byKey: specByKey } = useSkuSchemas();
    const specCols =
        specGroup && specGroup !== 'none'
            ? specFieldsOf(specByKey.get(specGroup))
            : [];
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    const [list, setList] = useState<SkuRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 批量操作 (2026-09-10): SKU 删除的动作名是 delete_sku, 不是通用 delete
    // 跨页勾选 (2026-10-13): 翻页保留已选 (按勾选导出要跨页挑), 改筛选才清空
    const skuCodes = useMemo(() => list.map((s) => s.skuCode), [list]);
    const sel = useBatchSelect(
        skuCodes,
        [kind, keyword, xcode, prop, status, hasMaterial, specGroup].join('|'),
        {
            crossPage: true,
        },
    );

    const [showExport, setShowExport] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    // 行级操作 (2026-08-21): 编辑弹窗 / 删除确认弹窗的目标行
    const [editSku, setEditSku] = useState<SkuRow | null>(null);
    // SKU 集合弹窗: null = 关, 'new' = 新建, 行 = 编辑
    const [bundleTarget, setBundleTarget] = useState<SkuRow | 'new' | null>(null);
    const [deleteSku, setDeleteSku] = useState<SkuRow | null>(null);
    // 二维码贴纸弹窗目标 (2026-09-07): 带全部绑定材料进弹窗, 多绑定时由用户选材质编码
    const [qrTarget, setQrTarget] = useState<SkuQrTarget | null>(null);
    // 副标题里的 "M 个材料": /v1/materials pageSize=1 只取 totalCount
    const [materialCount, setMaterialCount] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listSkus({
                kind,
                keyword: keyword || undefined,
                xcode: xcode || undefined,
                prop: prop || undefined,
                status: status || undefined,
                hasMaterial: hasMaterial || undefined,
                specGroup: specGroup || undefined,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [kind, keyword, xcode, prop, status, hasMaterial, specGroup, page]);

    useEffect(() => {
        load();
    }, [load]);

    // 浏览器后退/前进: 从 URL 还原 tab 与筛选 (同路由只改 query 不重挂组件)
    useRestoreQueryOnPop((q) => {
        setKind(q.get('kind') === SKU_KIND_BUNDLE ? SKU_KIND_BUNDLE : SKU_KIND_SINGLE);
        setInput(q.get('keyword') || '');
        setKeyword(q.get('keyword') || '');
        setXcode(q.get('xcode') || '');
        setProp(q.get('prop') || '');
        setStatus(q.get('status') || '');
        setHasMaterial(q.get('hasMaterial') || '');
        setSpecGroup(q.get('specGroup') || '');
        setPage(Number(q.get('page')) || 1);
    });

    useSyncQueryToUrl('/skus', {
        kind: isBundle ? kind : undefined,
        keyword,
        xcode,
        prop,
        status,
        hasMaterial,
        specGroup,
        page,
    });

    useEffect(() => {
        let cancelled = false;
        listMaterials({ page: 1, pageSize: 1 })
            .then((res) => {
                if (!cancelled) setMaterialCount(res.totalCount);
            })
            .catch(() => {
                if (!cancelled) setMaterialCount(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const next = input.trim();
        if (next === keyword && page === 1) {
            load();
            return;
        }
        setKeyword(next);
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // 集合组成里点子 SKU: 切到单品 tab 按编码搜 (SKU 无独立详情页, 单品行即全部信息).
    // push 一条历史 (筛选同步用的是 replace, 不 push 的话后退会直接离开 SKU 库, 回不到集合 tab)
    const viewSingleSku = (code: string) => {
        history.push(`/skus?keyword=${encodeURIComponent(code)}`);
        setKind(SKU_KIND_SINGLE);
        setProp('');
        setStatus('');
        setInput(code);
        setKeyword(code);
        setPage(1);
    };

    // 切 tab: 集合没有绑定材料 / 规格, 这两个筛选清掉 (否则切过去筛出空表)
    const switchKind = (k: SkuKind) => {
        if (k === kind) return;
        setKind(k);
        if (k === SKU_KIND_BUNDLE) {
            setHasMaterial('');
            setSpecGroup('');
            setXcode('');
        }
        setPage(1);
    };

    return (
        <div className="space-y-5">
            {/* 表头两行: 上行 标题 + 操作按钮 (按钮固定不压缩), 下行 筛选条 (窄屏自动换行).
                之前挤在一行, 窗口一窄按钮里的中文就被逐字竖排. */}
            <div className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="text-base font-semibold">SKU 库</div>
                        <div className="text-xs text-slate-500">
                            以 <b>SKU 为主键</b>(一物一码)平铺检索 · 共 {total}{' '}
                            个{isBundle ? ' SKU 集合' : ' SKU'} / {materialCount ?? '…'} 个材料 ·{' '}
                            <span className="text-slate-400">
                                与材料库同一张主数据, 仅视角不同
                            </span>
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            className="btn-ghost"
                            title="导出 Excel / CSV, 可附带二维码贴纸; 按当前筛选或勾选导出"
                            onClick={() => setShowExport(true)}
                        >
                            ⬇ 导出
                        </button>
                        <button
                            type="button"
                            className="btn-ghost"
                            title="批量导入 xlsx: 下载模板填好后上传, 已存在的 SKU 编号按更新处理"
                            onClick={() => setShowImport(true)}
                            hidden={!canUpdate || isBundle}
                        >
                            ⬆ 批量导入
                        </button>
                        <BatchActions
                            resource="material"
                            entityLabel="SKU"
                            deleteKeyField="skuCode"
                            deleteAction="delete_sku"
                            selected={sel.selectedKeys}
                            onClear={sel.clear}
                            onDone={load}
                        />
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() =>
                                isBundle ? setBundleTarget('new') : setShowCreate(true)
                            }
                            hidden={!canCreate}
                        >
                            {isBundle ? '+ 新增 SKU 集合' : '+ 新增 SKU'}
                        </button>
                    </div>
                </div>

                <div className="flex border-b border-slate-200">
                    {SKU_TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => switchKind(t.key)}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
                                kind === t.key
                                    ? 'border-brand-600 text-brand-600 font-medium'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <form
                    onSubmit={onSearch}
                    className="flex flex-wrap items-center gap-2"
                >
                    {xcode && (
                        <span className="tag bg-brand-50 text-brand-700 whitespace-nowrap">
                            材质 {xcode}
                            <button
                                type="button"
                                className="ml-1 hover:text-brand-900"
                                title="清除材质筛选"
                                onClick={() => {
                                    setXcode('');
                                    setPage(1);
                                }}
                            >
                                ✕
                            </button>
                        </span>
                    )}
                    <SkuPropFilter
                        className="sel w-28"
                        title="按 SKU 属性筛选"
                        value={prop}
                        onChange={(v) => {
                            setProp(v);
                            setPage(1);
                        }}
                    />
                    <div className="w-28" title="按在架状态筛选">
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => {
                                setStatus(v);
                                setPage(1);
                            }}
                            placeholder="全部状态"
                            options={SKU_STATUS_OPTS}
                            clearable
                        />
                    </div>
                    <div
                        hidden={isBundle}
                        className="w-32"
                        title="按是否绑定材料筛选 (对应“绑定材料”列)"
                    >
                        <SearchSelect
                            className="w-full"
                            value={hasMaterial}
                            onChange={(v) => {
                                setHasMaterial(v);
                                setPage(1);
                            }}
                            placeholder="全部绑定状态"
                            options={SKU_HAS_MATERIAL_OPTS}
                            clearable
                        />
                    </div>
                    <div
                        hidden={isBundle}
                        title="按规格类型筛选; 选定一个类型时该类型的规格字段展开成独立列"
                    >
                        <SearchSelect
                            className="w-36"
                            value={specGroup}
                            onChange={(v) => {
                                setSpecGroup(v);
                                setPage(1);
                            }}
                            placeholder="全部规格类型"
                            options={[
                                { value: 'none', label: '未选规格' },
                                ...specSchemas.map((g) => ({
                                    value: g.groupKey,
                                    label:
                                        g.label +
                                        (g.isEnabled ? '' : ' (已停用)'),
                                })),
                            ]}
                            clearable
                        />
                    </div>
                    <input
                        className="inp w-64 min-w-0 flex-1"
                        placeholder={isBundle ? '搜索 SKU编码 / 产品名' : '搜索 SKU编码 / 产品名 / 材质编码'}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onBlur={() => {
                            if (input.trim() === '' && keyword !== '') {
                                setKeyword('');
                                setPage(1);
                            }
                        }}
                    />
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                    {(prop || status || hasMaterial || specGroup || keyword) && (
                        <button
                            type="button"
                            className="btn-ghost text-slate-400"
                            title="清空筛选"
                            onClick={() => {
                                setProp('');
                                setStatus('');
                                setHasMaterial('');
                                setSpecGroup('');
                                setInput('');
                                setKeyword('');
                                setPage(1);
                            }}
                        >
                            ✕ 重置
                        </button>
                    )}
                </form>
                {sel.count > 0 && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                        <span>
                            已选 <b className="text-brand-700">{sel.count}</b>{' '}
                            个 SKU
                            {sel.otherPageCount > 0 && (
                                <span className="text-slate-400">
                                    {' '}
                                    (其中 {sel.otherPageCount} 个在其它页)
                                </span>
                            )}
                            <span className="text-slate-400">
                                {' '}
                                · 翻页保留, 改筛选条件会清空;
                                批量操作与按勾选导出作用于全部已选
                            </span>
                        </span>
                        <button
                            type="button"
                            className="text-brand-600 hover:underline"
                            onClick={sel.clear}
                        >
                            清空
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* overflow-x-auto: 窗口窄时横向滚动, 不把右侧 状态 / 操作 列裁掉 */}
            <div className="card overflow-x-auto">
                {isBundle ? (
                    <SkuBundleTable
                        list={list}
                        loading={loading}
                        sel={sel}
                        canDelete={canDelete}
                        canUpdate={canUpdate}
                        onEdit={setBundleTarget}
                        onViewItem={viewSingleSku}
                        onDelete={setDeleteSku}
                    />
                ) : (
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th">SKU 编码</th>
                            <th className="th">产品名</th>
                            <th className="th w-px whitespace-nowrap">属性</th>
                            {specCols.length > 0 ? (
                                specCols.map((f) => (
                                    <th key={f.key} className="th whitespace-nowrap" title={f.key}>
                                        {f.required && <span className="text-rose-500">* </span>}
                                        {f.label}
                                        {f.unit && <span className="text-slate-400 font-normal"> ({f.unit})</span>}
                                    </th>
                                ))
                            ) : (
                                <th className="th">规格</th>
                            )}
                            <th className="th">绑定材料</th>
                            <th className="th">适配工艺类型</th>
                            <th className="th">二维码</th>
                            <th className="th">状态</th>
                            <th className="th" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={9 + Math.max(1, specCols.length)}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={9 + Math.max(1, specCols.length)}
                                >
                                    暂无数据
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            list.map((s) => {
                                return (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="td" hidden={!canDelete}>
                                            <RowCheckbox
                                                hidden={!canDelete}
                                                sel={sel}
                                                k={s.skuCode}
                                            />
                                        </td>
                                        <td className="td font-mono text-xs whitespace-nowrap">
                                            {s.skuCode}
                                        </td>
                                        {/* 产品名是唯一允许换行的长文本列, 吃掉剩余宽度 */}
                                        <td className="td min-w-[12rem]">
                                            <div>
                                                {i18nText(
                                                    s.productName || {},
                                                ) || '—'}
                                            </div>
                                            {s.productName?.en && (
                                                <div className="text-[11px] text-slate-400">
                                                    {s.productName.en}
                                                </div>
                                            )}
                                        </td>
                                        <td className="td whitespace-nowrap">
                                            <SkuPropTag code={s.prop} />
                                        </td>
                                        {specCols.length > 0 ? (
                                            specCols.map((f) => {
                                                const t = specValueText(f, parseSpecParams(s.specParams)[f.key]);
                                                return (
                                                    <td key={f.key} className="td font-mono text-xs whitespace-nowrap">
                                                        {t ||
                                                            (f.required ? (
                                                                <span className="text-rose-500 font-sans">待补</span>
                                                            ) : (
                                                                <span className="text-slate-300">—</span>
                                                            ))}
                                                    </td>
                                                );
                                            })
                                        ) : (
                                            <td className="td">
                                                <SkuSpecText sku={s} />
                                            </td>
                                        )}
                                        {/* 绑定材料 / 适配工艺类型 两列逐行对齐: 同一材料在两列里各占一行 (SKU_BIND_ROW_CLS 固定行高) */}
                                        <td className="td text-xs">
                                            {(s.materials || []).length > 0 ? (
                                                <div>
                                                    {s.materials.map((m) => (
                                                        <div
                                                            key={m.xcode}
                                                            className={`${SKU_BIND_ROW_CLS} whitespace-nowrap`}
                                                        >
                                                            {m.isPrimary && (
                                                                <span
                                                                    className="text-amber-500 mr-0.5"
                                                                    title="主绑定 (扫码默认材料)"
                                                                >
                                                                    ★
                                                                </span>
                                                            )}
                                                            <PermissionLink
                                                                resource="material"
                                                                to={`/materials/${encodeURIComponent(m.xcode)}`}
                                                                className="text-brand-600 hover:underline font-mono"
                                                            >
                                                                {m.xcode}
                                                            </PermissionLink>{' '}
                                                            {/* 材料名超长省略 (悬停看全名), 不把整表撑宽 */}
                                                            <span
                                                                className="text-slate-500 inline-block max-w-[8rem] truncate align-bottom"
                                                                title={i18nText(
                                                                    m.name ||
                                                                        {},
                                                                )}
                                                            >
                                                                {i18nText(
                                                                    m.name ||
                                                                        {},
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="tag bg-amber-50 text-amber-700">
                                                    未绑定
                                                </span>
                                            )}
                                        </td>
                                        <td className="td">
                                            {(s.materials || []).length > 0 ? (
                                                <div>
                                                    {s.materials.map((m) => (
                                                        <div
                                                            key={m.xcode}
                                                            className={
                                                                SKU_BIND_ROW_CLS
                                                            }
                                                        >
                                                            {/* 该材料自己的工艺: 单行、最多 2 个, 其余 +N 悬停展开 (3 个会把表撑出 1280 屏宽) */}
                                                            <CapBadgesCell
                                                                caps={
                                                                    m.caps || []
                                                                }
                                                                limit={
                                                                    SKU_CAPS_LIMIT
                                                                }
                                                                nowrap
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-300">
                                                    —
                                                </span>
                                            )}
                                        </td>
                                        <td className="td text-xs whitespace-nowrap">
                                            {/* 二维码即时生成不落库: 一律给按钮, 未绑定材料的进弹窗看提示;
                                                多绑定时弹窗内下拉选按哪条材质编码出码 (默认主绑定) */}
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="生成二维码贴纸 (预览 / 下载 PNG)"
                                                onClick={() =>
                                                    setQrTarget({
                                                        skuCode: s.skuCode,
                                                        productName:
                                                            s.productName,
                                                        materials:
                                                            s.materials || [],
                                                    })
                                                }
                                            >
                                                ▦ 二维码
                                            </button>
                                        </td>
                                        <td className="td whitespace-nowrap">
                                            {s.status === 'on_shelf' ? (
                                                <span className="tag cap-on">
                                                    在售
                                                </span>
                                            ) : (
                                                <span className="tag bg-slate-100 text-slate-500">
                                                    下架
                                                </span>
                                            )}
                                        </td>
                                        <td
                                            className="td whitespace-nowrap text-xs"
                                            hidden={!canManage}
                                        >
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() => setEditSku(s)}
                                                hidden={!canUpdate}
                                            >
                                                编辑
                                            </button>
                                            <button
                                                type="button"
                                                className="ml-2 text-rose-500 hover:underline"
                                                onClick={() => setDeleteSku(s)}
                                                hidden={!canDelete}
                                            >
                                                删除
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                )}
            </div>

            <div className="text-[11px] text-slate-400">
                {isBundle ? (
                    <>
                        SKU 集合由若干单品 SKU × 数量组成 (如材料套装), 不直接绑定材料、不填规格;
                        被集合引用的单品 SKU 不能删除, 需先从集合中移除。
                    </>
                ) : (
                    <>
                        一个 SKU 可绑定多个材料(★ 为主绑定, 扫码等场景的默认材料);
                        适配工艺类型按绑定材料逐行对应(同一行即同一材料), 与材料库同源,
                        非 SKU 自身字段。
                    </>
                )}
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>

            <SkuExportModal
                open={showExport}
                preset={{
                    kind,
                    keyword: input.trim(),
                    xcode,
                    prop,
                    status,
                    hasMaterial,
                }}
                selectedCodes={sel.selectedKeys}
                onClose={() => setShowExport(false)}
            />

            <SkuCreateModal
                open={showCreate}
                presetXcode={xcode}
                onClose={() => setShowCreate(false)}
                onCreated={load}
            />

            <SkuBundleModal
                open={!!bundleTarget}
                sku={bundleTarget === 'new' ? null : bundleTarget}
                onClose={() => setBundleTarget(null)}
                onSaved={load}
            />

            <SkuImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onImported={load}
            />

            <SkuQrModal
                open={!!qrTarget}
                target={qrTarget}
                onClose={() => setQrTarget(null)}
            />

            <SkuEditModal
                open={!!editSku}
                sku={editSku}
                onClose={() => setEditSku(null)}
                onSaved={load}
            />

            <DeleteCascadeModal
                open={!!deleteSku}
                title={deleteSku?.kind === SKU_KIND_BUNDLE ? '删除 SKU 集合' : '删除 SKU'}
                targetLabel={deleteSku?.skuCode || ''}
                warning={
                    deleteSku?.kind === SKU_KIND_BUNDLE ? (
                        <>
                            只删除集合本身及其组成关系, 组成里的 {deleteSku.items?.length || 0} 个单品 SKU 不受影响。
                        </>
                    ) : (deleteSku?.materials || []).length > 0 ? (
                        <>
                            该 SKU 已绑定 {deleteSku?.materials.length} 个材料 (
                            <b>
                                {deleteSku?.materials
                                    .map((m) => m.xcode)
                                    .join('、')}
                            </b>
                            ), 删除后这些材料的 SKU 数各减 1
                            (材料本体不受影响)。
                            只想解除某个绑定请到对应材料详情用「解绑」。
                        </>
                    ) : undefined
                }
                onClose={() => setDeleteSku(null)}
                onConfirm={async () => {
                    if (!deleteSku) return;
                    const res = await deleteSkuGlobal(deleteSku.skuCode);
                    if (!alertApprovalIfPending(res.message)) {
                        toast(`SKU ${deleteSku.skuCode} 已删除`);
                        await load();
                    }
                }}
            />
        </div>
    );
}

export default SkuList;
